import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, ShieldCheck, ArrowLeft, CreditCard } from "lucide-react";
import Nav from "../site/Nav";
import Footer from "../site/Footer";
import { TOKEN_KEY, fmtDate, fmtMoney } from "../admin/adminUtils";
import { Stat } from "../admin/IntakeStat";
import AdminTokenGate from "../admin/AdminTokenGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  paid: "bg-olive/10 text-olive border-olive/30",
  pending: "bg-clay/10 text-clay-deep border-clay/30",
};

export default function AdminPaymentsPage() {
  const [token, setToken] = useState(() =>
    typeof window === "undefined" ? "" : window.sessionStorage.getItem(TOKEN_KEY) || "",
  );
  const [tokenInput, setTokenInput] = useState("");
  const [data, setData] = useState({ items: [], count: 0, paid_count: 0, total_paid_cents: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (bearer) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/payments?limit=300`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      setData(res.data || { items: [] });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Token rejected. Check ADMIN_TOKEN and try again.");
        window.sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError(err?.response?.data?.detail || "Could not load payments.");
      }
      setData({ items: [], count: 0, paid_count: 0, total_paid_cents: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const filtered = useMemo(() => {
    if (filter === "paid") return data.items.filter((i) => i.payment_status === "paid");
    if (filter === "pending") return data.items.filter((i) => i.payment_status !== "paid");
    return data.items;
  }, [data.items, filter]);

  if (!token) {
    return (
      <AdminTokenGate
        tokenInput={tokenInput}
        setTokenInput={setTokenInput}
        error={error}
        onUnlock={(t) => {
          window.sessionStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  const pills = [
    { id: "all", label: `All (${data.count})` },
    { id: "paid", label: `Paid (${data.paid_count})` },
    { id: "pending", label: `Pending (${data.count - data.paid_count})` },
  ];

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="admin-payments-page">
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay flex items-center gap-2">
                <ShieldCheck size={12} /> Admin · Payments
              </p>
              <h1 className="font-display mt-3 text-[2rem] sm:text-[2.4rem] md:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                Cleared payments
              </h1>
              <p className="mt-3 text-[14px] text-ink-muted leading-relaxed">
                Every Stripe checkout, newest first. Olive rows have cleared.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/admin/intakes"
                data-testid="admin-back-intakes"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink hover:border-clay/50 transition-colors min-h-[40px]"
              >
                <ArrowLeft size={13} /> Intakes
              </Link>
              <button
                onClick={() => load(token)}
                disabled={loading}
                data-testid="admin-payments-refresh"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink hover:border-clay/50 transition-colors min-h-[40px]"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <Stat label="Transactions" value={data.count} />
            <Stat label="Paid" value={data.paid_count} accent="olive" />
            <Stat
              label="Total cleared"
              value={fmtMoney(data.total_paid_cents, "USD")}
              accent="olive"
              testid="admin-payments-total"
            />
          </div>

          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {pills.map((p) => (
              <button
                key={p.id}
                onClick={() => setFilter(p.id)}
                data-testid={`admin-payments-filter-${p.id}`}
                className={`rounded-full px-4 py-2 text-[12.5px] transition-colors min-h-[40px] ${
                  filter === p.id
                    ? "bg-ink-deep text-sand-100 border border-ink-deep"
                    : "bg-sand-50 text-ink border border-sand-300 hover:border-clay/50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {error && <p className="mt-6 text-[13px] text-clay">{error}</p>}

          <div className="mt-6 rounded-3xl border border-sand-300 bg-sand-50 overflow-hidden">
            {filtered.length === 0 && !loading && (
              <p className="p-10 text-center text-ink-muted text-[14px]">
                No payments match this filter yet.
              </p>
            )}
            {filtered.map((it) => {
              const isPaid = it.payment_status === "paid";
              return (
                <div
                  key={it.session_id}
                  data-testid={`payment-row-${it.session_id}`}
                  className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-5 sm:px-6 py-4 border-b border-sand-300/70 last:border-b-0 ${
                    isPaid ? "bg-olive/[0.04]" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 sm:w-8 shrink-0">
                    <CreditCard size={15} className={isPaid ? "text-olive" : "text-ink-muted"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[15px] font-semibold text-ink truncate">
                      {it.package_name || it.lookup_key || "Package"}
                    </p>
                    <p className="text-[12.5px] text-ink-muted truncate">
                      {it.customer_email || "—"} · {fmtDate(it.paid_at || it.created_at)}
                    </p>
                  </div>
                  <div className="font-mono text-[14px] text-ink tabular-nums whitespace-nowrap">
                    {fmtMoney(it.amount_total || it.amount, it.currency)}
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium uppercase tracking-wide whitespace-nowrap ${
                      STATUS_STYLE[isPaid ? "paid" : "pending"]
                    }`}
                  >
                    {isPaid ? "Paid" : "Pending"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
